"use client";

import { memo, useCallback, useMemo } from "react";
import { X, FileText, Image, File } from "lucide-react";

export interface AttachedFile {
  id: string;
  file: File;
  name: string;
  size: number;
  type: string;
}

export interface FileUploadHandlers {
  onDragOver: (e: React.DragEvent) => void;
  onDragLeave: (e: React.DragEvent) => void;
  onDrop: (e: React.DragEvent) => void;
  onPaste: (e: React.ClipboardEvent) => void;
}

interface FileUploadHandlerProps {
  attachedFiles: AttachedFile[];
  setAttachedFiles: React.Dispatch<React.SetStateAction<AttachedFile[]>>;
}

// Memoized icons for better performance
const FileIcon = memo(({ type }: { type: string }) => {
  if (type.startsWith("image/")) return <Image className="w-4 h-4" />;
  if (type.includes("text") || type.includes("json") || type.includes("javascript")) return <FileText className="w-4 h-4" />;
  return <File className="w-4 h-4" />;
});
FileIcon.displayName = "FileIcon";

// Helper type for setting file list
export type SetAttachedFiles = React.Dispatch<React.SetStateAction<AttachedFile[]>>;

export const FileUploadHandler = memo(({
  attachedFiles,
  setAttachedFiles,
}: FileUploadHandlerProps) => {
  const formatFileSize = useCallback((bytes: number) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  }, []);

  const removeFile = useCallback((fileId: string) => {
    setAttachedFiles(prev => prev.filter(f => f.id !== fileId));
  }, [setAttachedFiles]);

  if (attachedFiles.length === 0) return null;

  return (
    <div className="flex flex-wrap gap-2 mt-2">
      {attachedFiles.map((file) => (
        <div
          key={file.id}
          className="flex items-center gap-2 bg-gray-100 dark:bg-gray-800 px-3 py-1.5 rounded-lg text-sm">
          <FileIcon type={file.type} />
          <span className="text-gray-700 dark:text-gray-300 truncate max-w-[150px]">{file.name}</span>
          <span className="text-gray-500 dark:text-gray-400 text-xs">({formatFileSize(file.size)})</span>
          <button
            type="button"
            onClick={() => removeFile(file.id)}
            className="ml-1 p-0.5 hover:bg-gray-200 dark:hover:bg-gray-700 rounded transition-colors">
            <X className="w-3 h-3 text-gray-500 dark:text-gray-400" />
          </button>
        </div>
      ))}
    </div>
  );
});
FileUploadHandler.displayName = "FileUploadHandler";

